import { Link, useLoaderData, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	createTemplatesPlansContentPresetImportConfiguration,
	getListTemplatesPlansContentPresetImportConfigurations,
} from "../../services/plans-template-content-preset-import-configuration.service";
import {
	deleteTemplatesPlansContentPresets,
	getListTemplatesPlansContentPresets,
	getTemplatesPlansContentPresets,
} from "../../services/plans-template-content-preset.service";

import ActionBtnItem from "../../components/utils/ActionBtnItem";
import ApplyContentPresetPopup from "../../components/popups/ApplyContentPresetPopup";
import Chip from "../../components/chip/Chip";
import ConfirmModal from "../../components/popups/ConfirmPopup";
import CrudPlanContentPresetsPopup from "../../components/popups/CrudPlanContentPresetsPopup";
import CrudPlanModal from "../../components/popups/CrudPlanPopup";
import DownloadMultipleModal from "../../components/popups/DownloadMultiplePopup";
import DuplicatePlanModal from "../../components/plans/DuplicatePlan";
import EmailMassiveModal from "../../components/popups/plan-template/EmailMassivePopup";
import FieldsPlanCrud from "../../components/popups/FieldsPlanCrud";
import ItemSummary from "../../components/plans/ItemSummary";
import LayoutNav from "../../components/layout/LayoutNav";
import LoaderPage from "../../components/utils/LoaderPage";
import { OrganizationsPickerContextProvider } from "../../context/organizations-picker.context";
import SectionBox from "../../components/section-box/SectionBox";
import StatusPlanCrud from "../../components/popups/StatusPlanCrud";
import SubHeader from "../../components/elements/SubHeader";
import TableConfEntities from "../../components/tables/TableConfEntities";
import TablePresetContents from "../../components/tables/TablePresetContents";
import { cn } from "../../lib/utils";
import { formatTemplatePlanDownloadObject } from "../../helper/formatter.helper";
import useNotification from "../../hooks/notification.hook";
import useTemplatePlanActionsHook from "../../hooks/use-template-plan-actions.hook";
import { useTranslation } from "react-i18next";
import { getTemplatesContentPresetsOrganizationTypes } from "../../services/plans-template.service";

const COPY_TITLE_SUFFIX = " (copia)";

const MAX_TITLE_LENGTH = 255;

// Durata dell'animazione di `ModalContainer`
const MODAL_TRANSITION_MS = 200;

/**
 * Promise wrapper for the callback based services
 */
const request = (service, body) => new Promise((resolve, reject) => service(body, resolve, reject));

/**
 * Build the duplicated preset title, respecting the max length of the field
 */
const buildCopyTitle = (title) =>
	`${title.slice(0, MAX_TITLE_LENGTH - COPY_TITLE_SUFFIX.length)}${COPY_TITLE_SUFFIX}`;

function PlanConfPreview() {
	const { t } = useTranslation();

	const navigate = useNavigate();

	const { openNotification, helperErrorNotification } = useNotification();

	const planData = useLoaderData();

	const [plan, setPlan] = useState(planData);

	const [preset, setPreset] = useState({});

	const [presetDefaults, setPresetDefaults] = useState(null);

	const [listOrganizations, setListOrganizations] = useState([]);

	const [listStatus, setListStatus] = useState(planData.requirement_statuses);

	const [forceUpdate, setForceUpdate] = useState(0);

	const [forceUpdateContentPresets, setForceUpdateContentPresets] = useState(0);

	const [orgTypes, setOrgTypes] = useState([]);

	const [contentPresetsOrgTypes, setContentPresetsOrgTypes] = useState([]);

	const [counterActiveOrgs, setCounterActiveOrgs] = useState(0);

	const [planFields, setPlanFields] = useState(
		planData.requirement_fields_settings
			? Object.entries(planData.requirement_fields_settings).map(([key, value]) => ({
					label: key,
					value,
				}))
			: []
	);

	const activeFields = useMemo(
		() => planFields.filter((i) => !!i.value),
		[JSON.stringify(planFields)]
	);

	const {
		createDownloadTemplateHandler,
		getAllOrganizationTypesHandler,
		getAllOrganizationsHandler,
		isLoading: isLoadingAction,
		isModalOpen,
		updateModalState,
		updateLoading,
		downloadDifferenceHandler,
		deletePlanHandler,
		getActiveOrganizationsCounterHandler,
	} = useTemplatePlanActionsHook();

	/**
	 * Get List organizations
	 */
	const updateActiveOrganizations = async () => {
		setListOrganizations([]);
		const organizations = await getAllOrganizationsHandler(planData);
		setListOrganizations(organizations);
	};

	/**
	 * Get types organizations
	 */
	const updateOrganizationsTypes = async () => {
		setOrgTypes([]);
		const types = await getAllOrganizationTypesHandler(planData);
		setOrgTypes(types);
	};

	const counterNewRequirements = useMemo(
		() => planData?.template_requirements_statuses?.find((el) => el.status === "new")?.count ?? 0,
		[planData.id]
	);

	const counterEditRequirements = useMemo(
		() =>
			planData?.template_requirements_statuses?.find((el) => el.status === "modified")?.count ?? 0,
		[planData.id]
	);

	/**
	 * Open email massive popup
	 */
	const openModalEmailHandler = () => {
		if (forceUpdate > 0 || !listOrganizations.length) {
			setForceUpdate(0);
			updateActiveOrganizations();
		}
		if (!orgTypes.length) {
			updateOrganizationsTypes();
		}

		updateModalState({ emailMassive: true });
	};

	/**
	 * Open download massive popup
	 */
	const openModalDownloadHandler = () => {
		if (forceUpdate > 0 || !listOrganizations.length) {
			setForceUpdate(0);
			updateActiveOrganizations();
		}

		if (!orgTypes.length) {
			updateOrganizationsTypes();
		}

		updateModalState({ downloadMassive: true });
	};

	const counters = [
		{ icon: "folder", value: planData.template_chapters_count },
		{
			icon: "text_box",
			value: planData.template_chapter_contents_count,
		},
		{ icon: "layers", value: planData.template_goals_count },
		{ icon: "layer", value: planData.template_requirements_count },
	];

	const updateCounterActiveOrganizations = async () => {
		const counter = await getActiveOrganizationsCounterHandler(planData.id);
		setCounterActiveOrgs(counter);
	};

	/**
	 * Delete handler
	 */
	const onDeleteHandler = () => {
		if (!isLoadingAction.deletePlan) {
			updateLoading({ deletePlan: true });
			deletePlanHandler(
				planData.id,
				() => {
					navigate("/admin/plans", { replace: true });
					updateModalState({ delete: false });
					updateLoading({ deletePlan: false });
				},
				() => {
					updateLoading({ deletePlan: false });
				}
			);
		}
	};

	const templateBottom = () => (
		<div className="flex justify-between items-center">
			<div className="flex gap-[16px]">
				{counters.map((counter, i) => (
					<div key={i} className="flex gap-[4px] items-center leading-[1em] font-bold">
						<span className={`icon_16 default ${counter.icon}`}></span>
						{counter.value}
					</div>
				))}
				<div className=" w-[1px] bg-dark-10"></div>

				<div className="flex gap-[16px]">
					<div className="flex gap-[4px] items-center leading-[1em] font-bold">
						<span className="icon_16 default megaphone icon_mask !bg-secondary-100"></span>
						{counterNewRequirements}
					</div>

					<div className="flex gap-[4px] items-center leading-[1em] font-bold">
						<span className="icon_16 default update icon_mask !bg-secondary-100"></span>
						{counterEditRequirements}
					</div>
				</div>

				<div className=" w-[1px] bg-dark-10"></div>
				<div className="flex gap-[4px] items-center leading-[1em]">
					<span className="text-dark-70">Attivo:</span>
					<span
						className={cn(
							"icon_16 default icon_mask",
							plan.active ? "check !bg-green-100" : "cross !bg-red-100"
						)}
					></span>
					<span className="font-bold">{plan.active ? "Si" : "No"}</span>
				</div>

				<div className="flex gap-[4px] items-center leading-[1em]">
					<span className="text-dark-70">In lavorazione:</span>
					<span
						className={cn(
							"icon_16 default icon_mask",
							plan.working ? "check !bg-green-100" : "cross !bg-red-100"
						)}
					></span>
					<span className="font-bold">{plan.working ? "Si" : "No"}</span>
				</div>
			</div>

			<Link
				to={`/admin/plans/template/configuration/${planData.id}`}
				className="button fill secondary small"
			>
				<span className="icon_24 default wrench icon_mask !bg-primary-100"></span>
				Configura
			</Link>
		</div>
	);

	const templateHeader = () => (
		<div className="flex gap-4 items-center">
			<ActionBtnItem icon="delete" onClick={() => updateModalState({ delete: true })} big />
			<ActionBtnItem icon="edit" onClick={() => updateModalState({ crud: true })} big />
			<ActionBtnItem icon="duplicate" onClick={() => updateModalState({ duplicate: true })} big />
		</div>
	);

	const templateRight = () => (
		<div className="flex flex-wrap gap-3">
			{plan?.tags?.map((tag) => (
				<Chip
					label={tag.label}
					key={tag.id}
					className="text-white font-semibold ring-0"
					style={{ backgroundColor: tag.color }}
				/>
			))}
		</div>
	);

	/**
	 * Get item template content presets
	 */
	const getItemContentPresets = (presetId, scope) => {
		const body = { templatePlan: plan.id, contentPreset: presetId };

		if (isLoadingAction?.getContentPreset) return;

		updateLoading({ getContentPreset: true });

		// Fetch Api
		getTemplatesPlansContentPresets(
			body,
			(res) => {
				setPreset(res.data);
				updateLoading({ getContentPreset: false });

				switch (scope) {
					case "edit":
						updateModalState({ crudContentPreset: true });
						break;
					case "delete":
						updateModalState({ deletePreset: true });
						break;
				}
			},
			() => {
				updateLoading({ getContentPreset: false });
			}
		);
	};

	/**
	 * Delete item template content presets
	 */
	const onDeleteItemContentPresetsHandler = useCallback(() => {
		if (isLoadingAction?.deletePreset) return;

		updateLoading({ deletePreset: true });

		// Delete Api
		deleteTemplatesPlansContentPresets(
			{ templatePlan: plan.id, contentPreset: preset.id },
			() => {
				updateLoading({ deletePreset: false });
				updateModalState({ deletePreset: false });
				setForceUpdateContentPresets((f) => f + 1);
				setAllContentPresets((prevState) => prevState.filter((item) => item.id !== preset.id));
				setPreset({});
			},
			() => {
				updateLoading({ deletePreset: false });
			}
		);
	}, [preset]);

	/**
	 * Copy all import configurations from a preset to another one
	 */
	const copyContentPresetConfigurations = async (sourcePresetId, targetPresetId) => {
		const configurations = [];

		const getPage = async (page = 1) => {
			const res = await request(getListTemplatesPlansContentPresetImportConfigurations, {
				params: {
					templatePlan: plan.id,
					contentPreset: sourcePresetId,
					per_page: 100,
					page,
				},
			});

			configurations.push(...res.data);

			if (res.meta.current_page < res.meta.last_page) await getPage(res.meta.current_page + 1);
		};

		await getPage();

		for (const configuration of configurations) {
			await request(createTemplatesPlansContentPresetImportConfiguration, {
				templatePlan: plan.id,
				contentPreset: targetPresetId,
				title: configuration.title,
				selection_mode: configuration.selection_mode,
				content_selections: configuration.content_selections,
				requirement_selections: configuration.requirement_selections,
			});
		}
	};

	/**
	 * Duplicate item template content presets
	 * Open the crud modal prefilled with the data of the source preset
	 */
	const onCopyItemContentPresetsHandler = async (presetId) => {
		if (isLoadingAction?.getContentPreset) return;

		updateLoading({ getContentPreset: true });

		try {
			// Get preset source
			const source = await request(getTemplatesPlansContentPresets, {
				templatePlan: plan.id,
				contentPreset: presetId,
			});

			setPreset({});
			setPresetDefaults({
				title: buildCopyTitle(source.data.title),
				organization_type_id: source.data.organization_type_id,
				description: source.data.description,
				active: source.data.active,
				sourcePreset: { id: source.data.id, label: source.data.title },
			});
			updateModalState({ crudContentPreset: true });
		} catch (error) {
			helperErrorNotification(error);
		} finally {
			updateLoading({ getContentPreset: false });
		}
	};

	/**
	 * Get List template contents preset
	 * Lazy: fetched once, only when needed by the crud modal or the apply preset action
	 */
	const [allContentPresets, setAllContentPresets] = useState([]);
	const [hasContentPresets, setHasContentPresets] = useState(false);
	const isContentPresetsLoaded = useRef(false);
	const getListContentPresets = () => {
		if (isContentPresetsLoaded.current || !hasContentPresets) return;

		isContentPresetsLoaded.current = true;

		const body = {
			...(plan && { templatePlan: plan.id }),
			per_page: 100,
		};

		updateLoading({ listContentPresets: true });

		// Fetch Api
		getListTemplatesPlansContentPresets(
			body,
			(res) => {
				setAllContentPresets(res.data);
				updateLoading({ listContentPresets: false });
			},
			() => {
				isContentPresetsLoaded.current = false;
				updateLoading({ listContentPresets: false });
			}
		);
	};

	const getListContentPresetsOrganizationTypes = () => {
		if (isLoadingAction.contentPresetOrganizationTypes) return;

		updateLoading({ contentPresetOrganizationTypes: true });

		// Fetch Api
		getTemplatesContentPresetsOrganizationTypes(
			{},
			(res) => {
				const data = Array.isArray(res?.data) ? res.data : [];
				const reordered = [...data].sort((a, b) => (b?.is_master ? 1 : 0) - (a?.is_master ? 1 : 0));

				setContentPresetsOrgTypes(reordered);
				updateLoading({ contentPresetOrganizationTypes: false });
			},
			() => {
				updateLoading({ contentPresetOrganizationTypes: false });
			}
		);
	};

	/**
	 * Group presets by organization type
	 * Ogni gruppo: { id, title, listItems } (formato accettato da `SingleSelect`)
	 */
	const reducePresetsForType = (contentPresets) => {
		return contentPresets.reduce((acc, item) => {
			const type = item.organization_type;
			const id = type?.id ?? item.organization_type_id;
			const key = `g_${id}`; // chiave NON numerica -> ordine di inserimento preservato

			if (!acc[key]) {
				acc[key] = {
					id,
					title: type?.name ?? "",
					is_master: type?.is_master ?? false,
					listItems: [],
				};
			}

			acc[key].listItems.push({ id: item.id, label: item.title });

			return acc;
		}, {});
	};

	const groupedContentPresets = useMemo(
		() => reducePresetsForType(allContentPresets),
		[allContentPresets]
	);

	/**
	 * Apply preset: apre la modale di selezione del preset per l'ente scelto
	 */
	const [applyPresetOrganization, setApplyPresetOrganization] = useState(null);

	/**
	 * Handoff tra "Applica set dati" e la crud dei preset: la seconda modale si apre
	 * a chiusura completata della prima, così non si sovrappongono
	 */
	const resumeApplyPreset = useRef(false);
	const modalHandoff = useRef(null);

	const openAfterClose = (open) => {
		clearTimeout(modalHandoff.current);
		modalHandoff.current = setTimeout(open, MODAL_TRANSITION_MS);
	};

	useEffect(() => () => clearTimeout(modalHandoff.current), []);

	const handleApplyPreset = (organization) => {
		setApplyPresetOrganization(organization);
		getListContentPresets();
		updateModalState({ applyPreset: true });
	};

	/**
	 * Nessun preset per la tipologia dell'ente: crea il preset e torna all'applicazione
	 */
	const onCreatePresetFromApply = (organizationTypeId) => {
		setPreset({});
		setPresetDefaults({ organization_type_id: organizationTypeId });

		resumeApplyPreset.current = true;
		updateModalState({ applyPreset: false });
		openAfterClose(() => updateModalState({ crudContentPreset: true }));
	};

	/**
	 * Chiusura crud preset: torna all'applicazione del set dati
	 */
	const onCloseCrudContentPreset = () => {
		if (!resumeApplyPreset.current) return;

		resumeApplyPreset.current = false;
		openAfterClose(() => updateModalState({ applyPreset: true }));
	};

	/**
	 * Vai alla copia dei contenuti del preset selezionato sull'ente
	 */
	const onConfirmApplyPreset = (contentPreset) => {
		const organizationPlanId = applyPresetOrganization?.organization_plans?.[0]?.id;

		if (!contentPreset || !organizationPlanId) return;

		updateModalState({ applyPreset: false });

		navigate(
			`/admin/plans/templates/${plan.id}/content-presets/${contentPreset.id}/copy/${applyPresetOrganization.id}/${organizationPlanId}`
		);
	};

	useEffect(() => {
		updateCounterActiveOrganizations();
		updateOrganizationsTypes();
		setPlan(planData);
		isContentPresetsLoaded.current = false;
		setAllContentPresets([]);
		setHasContentPresets(false);
		getListContentPresetsOrganizationTypes();
	}, [planData.id]);

	return (
		<LayoutNav>
			<SubHeader title={plan.name} back TemplateRightContent={templateHeader} />

			<div className="main_page_box grid_layout !gap-[24px]">
				<SectionBox>
					<ItemSummary
						isIconBg={true}
						icon={`${!planData.diff_download?.length ? "piano" : "branch"}`}
						title={plan.description}
						templateBottom={templateBottom}
						templateRight={templateRight}
					/>
				</SectionBox>

				<div className="grid_page_right_side">
					<div className="left_content">
						<SectionBox
							className={{ content: "!p-0 divide-y divide-dark-10" }}
							title="Gestione piano"
						>
							<BoxAction
								icon="circle_arrow"
								actionHandler={() => updateModalState({ statusConfiguration: true })}
								title="Stati delle linee d'azione"
								ctaLabel="Gestisci stati"
								className="flex items-center flex-wrap gap-[12px]"
							>
								{listStatus.map((status, index) => (
									<Chip
										key={index}
										label={status.name}
										icon={cn("icon_16 status", status.icon)}
										innerChip={status.default ? "Predefinito" : status.done ? "Completamento" : ""}
									/>
								))}
							</BoxAction>

							<BoxAction
								icon="wrench"
								actionHandler={() => navigate(`/admin/plans/template/${planData.id}/presets`)}
								title="Configurazioni di stampa"
								ctaLabel="Gestisci configurazioni"
								className="flex items-center flex-wrap gap-[12px]"
							>
								<div className="max-w-[600px] text-dark-70">
									Gestisci le configurazioni di stampa: la selezione degli stati, l'inclusione dello
									scadenziario e adozioni, la selezione dei contenuti da includere
								</div>
							</BoxAction>

							<BoxAction
								icon="layer"
								actionHandler={() => updateModalState({ fieldsConfiguration: true })}
								title="Campi delle linee d’azioni per la compilazione"
								ctaLabel="Modifica campi"
								className="flex items-center flex-wrap gap-[12px]"
							>
								{!!activeFields.length &&
									activeFields.map((field) => (
										<Chip key={field.label} label={t(`common:fields.${field.label}`)} />
									))}
							</BoxAction>
						</SectionBox>

						<SectionBox title="Enti">
							<TableConfEntities
								key={planData.id}
								plan={planData}
								organizationTypesFilter={{
									list: orgTypes,
									loading: isLoadingAction.organizationTypes,
								}}
								onToggleChange={() => {
									updateCounterActiveOrganizations();
									setForceUpdate((f) => f + 1);
								}}
								onAppyPreset={handleApplyPreset}
							/>
						</SectionBox>

						<SectionBox title="Preset contenuti">
							<TablePresetContents
								key={planData.id}
								plan={planData}
								handleCreate={() => {
									setPreset({});
									setPresetDefaults(null);
									getListContentPresets();
									updateModalState({ crudContentPreset: true });
								}}
								handleEdit={(presetSelected) => {
									setPreset({});
									setPresetDefaults(null);
									getListContentPresets();
									getItemContentPresets(presetSelected, "edit");
								}}
								handleCopy={(presetSelected) => {
									getListContentPresets();
									onCopyItemContentPresetsHandler(presetSelected);
								}}
								handleDelete={(presetSelected) => {
									setPreset(presetSelected);
									getItemContentPresets(presetSelected, "delete");
								}}
								onListUpdate={(list) => setHasContentPresets(list.length > 0)}
								forceUpdate={forceUpdateContentPresets}
							/>
						</SectionBox>
					</div>
					<div className="right_content sticky top-[152px] h-fit">
						<SectionBox title="Azioni massive">
							<div className="flex justify-between items-center">
								<div className="flex items-center gap-[4px] font-bold">
									<span className="icon_24 default entity icon_mask !bg-dark-70"></span>
									Enti abilitati
								</div>
								{isLoadingAction.activeOrganizationCounter ? (
									<div className="icon_16 default loader"></div>
								) : (
									<span className="text-20 font-bold leading-[1em]">{counterActiveOrgs}</span>
								)}
							</div>

							<div className="pt-[16px] grid gap-[16px]">
								<button type="button" className="button line small" onClick={openModalEmailHandler}>
									<span className="icon_24 default send icon_mask !bg-dark-70"></span>
									Invio massivo piani
								</button>

								<button
									type="button"
									className="button line small"
									onClick={openModalDownloadHandler}
								>
									<span className="icon_24 default download icon_mask !bg-dark-70"></span>
									Genera e scarica piani
								</button>
							</div>
						</SectionBox>

						<SectionBox title="Documentazione" className={{ content: "divide-y divide-dark-10" }}>
							{/* Download clone diff */}
							{planData?.diff_download && planData.diff_filename && (
								<div className="flex justify-between py-[16px] first:pt-0 last:pb-0">
									<div className="flex items-center gap-[4px] font-bold">
										<span className="icon_24 default download_diff flex flex-none icon_mask !bg-dark-70" />
										Differenze della duplicazione
									</div>

									<button
										type="button"
										className="link-interactive"
										onClick={() =>
											downloadDifferenceHandler(planData.diff_download, planData.diff_filename)
										}
									>
										Scarica
									</button>
								</div>
							)}

							{/* Download fronte */}
							<div className="py-[16px] first:pt-0 last:pb-0">
								<div className="flex items-center gap-[4px] font-bold">
									<span className="icon_24 default doc flex flex-none icon_mask !bg-dark-70" />
									Frontespizio del piano
								</div>

								{plan.frontispiece && (
									<div className="flex bg-interactive-10 p-[11px_12px] justify-between rounded-[8px] text-interactive-100 font-semibold text-[12px] gap-[8px] mt-[10px]">
										<div className="flex gap-[2px] items-center w-0 flex-1">
											<div className="icon_16 default paper_clip flex-none icon_mask !bg-interactive-100"></div>
											<div className="truncate">{plan.frontispiece.name}</div>
										</div>
										<ActionBtnItem
											icon="download"
											onClick={() =>
												downloadDifferenceHandler(
													plan.frontispiece.original_url,
													plan.frontispiece.file_name
												)
											}
											tooltip="Scarica"
										/>
									</div>
								)}
							</div>

							{/* Download template */}
							<div className="flex justify-between py-[16px] first:pt-0 last:pb-0">
								<div className="flex items-center gap-[4px] font-bold">
									<span className="icon_24 default doc flex flex-none icon_mask !bg-dark-70" />
									Template del piano
								</div>

								<button
									type="button"
									className="link-interactive"
									onClick={() => createDownloadTemplateHandler(planData)}
								>
									Scarica
								</button>
							</div>
						</SectionBox>
					</div>
				</div>
			</div>

			<OrganizationsPickerContextProvider organizations={listOrganizations} types={orgTypes}>
				<EmailMassiveModal
					open={isModalOpen.emailMassive}
					setOpen={(open) => updateModalState({ emailMassive: open })}
					loadingData={{
						organizations: isLoadingAction.activeOrganization,
						types: isLoadingAction.organizationTypes,
					}}
				/>
			</OrganizationsPickerContextProvider>

			<DownloadMultipleModal
				organizations={listOrganizations}
				organizationTypes={orgTypes}
				open={isModalOpen.downloadMassive}
				setOpen={(open) => updateModalState({ downloadMassive: open })}
				template={formatTemplatePlanDownloadObject(planData.contents)}
				loadingData={{
					organizations: isLoadingAction.activeOrganization,
					types: isLoadingAction.organizationTypes,
				}}
				statuses={listStatus}
				templateId={planData.id}
			/>

			<StatusPlanCrud
				open={isModalOpen.statusConfiguration}
				setOpen={(open) => updateModalState({ statusConfiguration: open })}
				planTemplate={planData}
				onListUpdate={(list) => setListStatus(list)}
			/>

			<FieldsPlanCrud
				open={isModalOpen.fieldsConfiguration}
				setOpen={(open) => updateModalState({ fieldsConfiguration: open })}
				planTemplate={planData}
				onSuccess={setPlanFields}
			/>

			<ConfirmModal
				open={isModalOpen.delete}
				setOpen={(open) => updateModalState({ delete: open })}
				title="Conferma eliminazione"
				icon="icon_16 default piano"
				subtitle={plan?.name}
				description={plan?.description && plan.description}
				counterCaphters={planData?.chapters_count && planData.chapters_count}
				counterContents={planData?.chapter_contents_count && planData.chapter_contents_count}
				counterGoals={planData?.goals_count && planData.goals_count}
				counterActions={planData?.requirements_count && planData.requirements_count}
				alertMessage="Attenzione! Questa operazione è irreversibile! <b>Sei sicuro di procedere?</b>"
				ctaLabel="Elimina"
				onConfirm={onDeleteHandler}
			/>

			<CrudPlanModal
				open={isModalOpen.crud}
				setOpen={(bool) => updateModalState({ crud: bool })}
				planModel={plan}
				onEdit={(item) => {
					updateModalState({ crud: false });
					setPlan(item.data);
				}}
			/>

			<DuplicatePlanModal
				open={isModalOpen.duplicate}
				setOpen={(open) => updateModalState({ duplicate: open })}
				planModel={plan}
				onSuccess={(item) => {
					updateModalState({ duplicate: false });
					navigate(`/admin/plans/template/detail/${item.data.id}`);
				}}
			/>

			{/* POPUP • Plan content presets • crud */}
			<CrudPlanContentPresetsPopup
				open={isModalOpen.crudContentPreset}
				setOpen={(bool) => {
					updateModalState({ crudContentPreset: bool });
				}}
				planData={plan}
				contentPresetsModel={preset}
				presetDefaults={presetDefaults}
				organizationTypes={contentPresetsOrgTypes}
				contentPresetsGrouped={groupedContentPresets}
				isLoadingContentPresets={isLoadingAction?.listContentPresets}
				onEdit={(item) => {
					const edited = item?.data ?? item;
					setAllContentPresets((prevState) =>
						prevState.some((p) => p.id === edited.id)
							? prevState.map((p) => (p.id === edited.id ? edited : p))
							: [edited, ...prevState]
					);
					updateModalState({ crudContentPreset: false });
					setForceUpdateContentPresets((f) => f + 1);
				}}
				onInsert={(item, sourcePresetId) => {
					const inserted = item?.data ?? item;

					navigate(`/admin/plans/templates/${plan.id}/content-presets/${inserted.id}/compile`);
				}}
			/>

			{/* POPUP • Plan content presets • apply */}
			<ApplyContentPresetPopup
				open={isModalOpen.applyPreset}
				setOpen={(bool) => updateModalState({ applyPreset: bool })}
				contentPresetsGrouped={groupedContentPresets}
				organization={applyPresetOrganization}
				isLoading={isLoadingAction?.listContentPresets}
				onConfirm={onConfirmApplyPreset}
				onCreatePreset={onCreatePresetFromApply}
			/>

			{/* POPUP • Plan content presets • delete */}
			<ConfirmModal
				open={isModalOpen.deletePreset}
				setOpen={(open) => updateModalState({ deletePreset: open })}
				title="Conferma eliminazione preset"
				subtitle={preset?.name}
				description={preset?.description && preset.description}
				alertMessage="Attenzione! Questa operazione è irreversibile! <b>Sei sicuro di procedere?</b>"
				ctaLabel="Elimina"
				isLoading={isLoadingAction?.deletePreset}
				onConfirm={onDeleteItemContentPresetsHandler}
			/>

			{isLoadingAction.downloadTemplate && <LoaderPage title="Generazione file in corso..." />}

			{isLoadingAction.copyPreset && <LoaderPage title="Duplicazione preset in corso..." />}
		</LayoutNav>
	);
}

const BoxAction = ({ actionHandler, title, children, className, ctaLabel, icon }) => {
	return (
		<div className="p-[24px]">
			<div className="flex justify-between items-center gap-[16px]">
				<div className="flex gap-[4px] items-center text-16 font-bold">
					<div className={cn("icon_24 default icon_mask !bg-dark-50", icon)} />
					{title}
				</div>
				<button type="button" className="button fill primary small" onClick={actionHandler}>
					{ctaLabel}
				</button>
			</div>
			{children && <div className={cn("pt-[20px]", className)}>{children}</div>}
		</div>
	);
};

export default PlanConfPreview;
